// Sponsorship fulfillment and renewals (rentals and sponsorships plan,
// Phase 7; ADR 0046). Booking stays the record of each sponsorship and,
// since its 0112, of each deliverable (a row of its own, no longer a
// position in a JSON list). Hub shows them as work, keeps the evidence, and
// asks Booking to change them by command.
//
// Booking's reads (bearer-only on HUB_READ_SECRET, SELECT only):
//   GET /api/internal/hub/v1/opportunities                  packages and event tiers with capacity left, bundles
//   GET /api/internal/hub/v1/interest?status=               interest submissions and their items
//   GET /api/internal/hub/v1/renewals?within_days=          sponsorships whose term ends or renewal window opens soon
//   GET /api/internal/hub/v1/deliverables?status=&owner=&due_before=   every active deliverable
//   GET /api/internal/hub/v1/sponsorships/:id/deliverables  one sponsorship's deliverables
//
// Decision 12: trade is shown as trade value, never as revenue. Booking sends
// cash and trade as separate fields, and every Hub total here keeps them apart.

import { z } from 'zod'
import type { LookupHit, LookupResult } from './source-lookup'

export const BOOKING_OPPORTUNITIES_PATH = '/opportunities'
export const BOOKING_INTEREST_PATH = '/interest'
export const BOOKING_RENEWALS_PATH = '/renewals'
export const BOOKING_DELIVERABLES_PATH = '/deliverables'
export const bookingSponsorshipDeliverablesPath = (id: string) => `/sponsorships/${encodeURIComponent(id)}/deliverables`

const Id = z.string().min(1).max(64)
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const Iso = z.string().max(40)
const Text = (max: number) => z.string().max(max)
const Minor = z.number().int()
const Version = z.string().max(20)
const Compensation = z.enum(['cash', 'trade', 'cash_and_trade'])

// ------------------------------------------------------------ opportunities

export const BookingOpportunities = z.object({
  version: z.literal(1),
  asOf: Iso,
  packages: z.array(
    z.object({
      sourceId: Id,
      name: Text(200),
      description: Text(2000).nullable(),
      category: Text(100).nullable(),
      scope: Text(40),
      active: z.boolean(),
      exclusive: z.boolean(),
      capacity: z.number().int().nullable(),
      used: z.number().int(),
      softHolds: z.number().int(),
      left: z.number().int().nullable(),
      standardPriceMinor: Minor.nullable(),
      monthlyPriceMinor: Minor.nullable(),
      termMonths: z.number().int().nullable(),
      renewalEligible: z.boolean(),
      defaultRenewalType: Text(40),
      deliverableTemplates: z.array(Text(500)),
    }),
  ),
  eventTiers: z.array(
    z.object({
      sourceId: Id,
      breEvent: z.object({ sourceId: Id, name: Text(200), date: Day.nullable(), status: Text(40).nullable() }),
      name: Text(200),
      description: Text(2000).nullable(),
      active: z.boolean(),
      capacity: z.number().int().nullable(),
      used: z.number().int(),
      softHolds: z.number().int(),
      left: z.number().int().nullable(),
      priceMinor: Minor.nullable(),
    }),
  ),
  bundles: z.array(z.object({ sourceId: Id, name: Text(200), sponsorName: Text(200).nullable(), status: Text(40), faceValueMinor: Minor.nullable(), priceMinor: Minor.nullable() })),
})
export type BookingOpportunities = z.infer<typeof BookingOpportunities>

export const INTEREST_STATUSES = ['new', 'reviewed', 'converted', 'closed'] as const
export const BookingInterestList = z.object({
  version: z.literal(1),
  asOf: Iso,
  submissions: z.array(
    z.object({
      sourceId: Id,
      sponsorName: Text(200),
      company: Text(200).nullable(),
      notes: Text(4000).nullable(),
      status: z.enum(INTEREST_STATUSES),
      submittedAt: Iso.nullable(),
      updatedAt: Iso.nullable(),
      items: z.array(
        z.object({
          sourceId: Id,
          itemType: z.enum(['package', 'event_tier']),
          label: Text(300),
          breEventId: Id.nullable(),
          tierName: Text(200).nullable(),
          packageId: Id.nullable(),
          status: Text(40),
          holdExpiresAt: Iso.nullable(),
          waitlistPosition: z.number().int().nullable(),
        }),
      ),
    }),
  ),
})
export type BookingInterestList = z.infer<typeof BookingInterestList>

// ----------------------------------------------------------------- renewals

export const BookingRenewal = z.object({
  sourceId: Id,
  version: Version,
  title: Text(200),
  status: Text(40),
  packageName: Text(200).nullable(),
  eventName: Text(200).nullable(),
  compensation: Compensation,
  // Never added together (decision 12).
  cashMinor: Minor.nullable(),
  tradeValueMinor: Minor.nullable(),
  billingCadence: z.enum(['annual', 'monthly']),
  startDate: Day.nullable(),
  endDate: Day.nullable(),
  owner: z.object({ sub: z.string().max(200), name: Text(200).nullable() }).nullable(),
  renewal: z.object({ type: Text(40), status: Text(40).nullable(), date: Day.nullable(), windowStart: Day.nullable(), windowEnd: Day.nullable(), notes: Text(2000).nullable(), acceptedAt: Iso.nullable() }),
  renewedFromId: Id.nullable(),
  renewalRecord: z.object({ sourceId: Id, status: Text(40), version: Version, createdAt: Iso.nullable() }).nullable(),
})
export type BookingRenewal = z.infer<typeof BookingRenewal>
export const BookingRenewals = z.object({ version: z.literal(1), asOf: Iso, from: Day, to: Day, renewals: z.array(BookingRenewal) })
export type BookingRenewals = z.infer<typeof BookingRenewals>

// Where a renewal stands, derived (never stored), in the order staff work them:
//   accepted     the sponsor accepted in myBRE, or Booking says exercised
//   offered      a renewal record exists in Booking (the offer), not yet accepted
//   window_open  the renewal window has opened and nothing is offered yet
//   due          the term ends (or the window opens) within the lead time
//   waived       BRE or the sponsor declined
//   lapsed       the term ended with nothing offered
export const RENEWAL_STAGES = ['due', 'window_open', 'offered', 'accepted', 'waived', 'lapsed'] as const
export type RenewalStage = (typeof RENEWAL_STAGES)[number]
export const RENEWAL_LEAD_DAYS = 90

const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)

/** The day the renewal is due: the window's start, Booking's renewal date, or 90 days before the term ends. */
export function renewalDueOn(r: Pick<BookingRenewal, 'renewal' | 'endDate'>): string | null {
  return r.renewal.windowStart ?? r.renewal.date ?? (r.endDate ? addDays(r.endDate, -RENEWAL_LEAD_DAYS) : null)
}

export function renewalStage(r: BookingRenewal, today: string): RenewalStage {
  if (r.renewal.acceptedAt || r.renewal.status === 'exercised') return 'accepted'
  if (r.renewal.status === 'waived') return 'waived'
  if (r.renewalRecord && r.renewalRecord.status !== 'cancelled') return 'offered'
  if (r.endDate && r.endDate < today) return 'lapsed'
  if (r.renewal.windowStart && r.renewal.windowStart <= today) return 'window_open'
  return 'due'
}

export type RenewalRow = BookingRenewal & { stage: RenewalStage; dueOn: string | null; overdue: boolean }

export function renewalsPipeline(list: BookingRenewal[], today: string) {
  const rows: RenewalRow[] = list.map((r) => {
    const stage = renewalStage(r, today)
    const dueOn = renewalDueOn(r)
    return { ...r, stage, dueOn, overdue: (stage === 'due' || stage === 'window_open') && dueOn !== null && dueOn < today }
  })
  rows.sort((a, b) => RENEWAL_STAGES.indexOf(a.stage) - RENEWAL_STAGES.indexOf(b.stage) || (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999') || a.title.localeCompare(b.title))
  const byStage = Object.fromEntries(RENEWAL_STAGES.map((s) => [s, rows.filter((r) => r.stage === s)])) as Record<RenewalStage, RenewalRow[]>
  const open = rows.filter((r) => r.stage === 'due' || r.stage === 'window_open' || r.stage === 'offered')
  return { rows, byStage, summary: { open: open.length, overdue: rows.filter((r) => r.overdue).length, ...sponsorshipValue(open) } }
}

/**
 * Cash and trade, kept apart (decision 12). "Revenue" here is cash only;
 * trade is its own figure and is never added to it.
 */
export function sponsorshipValue(list: { compensation: 'cash' | 'trade' | 'cash_and_trade'; cashMinor: number | null; tradeValueMinor: number | null }[]) {
  let cashMinor = 0
  let tradeValueMinor = 0
  let cashUnknown = 0
  for (const s of list) {
    if (s.compensation !== 'trade') {
      if (s.cashMinor === null) cashUnknown += 1
      else cashMinor += s.cashMinor
    }
    if (s.compensation !== 'cash' && s.tradeValueMinor !== null) tradeValueMinor += s.tradeValueMinor
  }
  return { cashMinor, tradeValueMinor, cashUnknown }
}

// ------------------------------------------------------------- deliverables

export const DELIVERABLE_STATUSES = ['open', 'in_progress', 'fulfilled', 'waived'] as const
export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number]
export const DELIVERABLE_KINDS = ['standard', 'ticket_allotment'] as const
export const EVIDENCE_KINDS = ['document', 'photo', 'link', 'btowntix_order'] as const
export type EvidenceKind = (typeof EVIDENCE_KINDS)[number]

export const BookingDeliverable = z.object({
  sourceId: Id,
  sponsorship: z.object({ sourceId: Id, title: Text(200), status: Text(40), compensation: Compensation, ownerSub: z.string().max(200).nullable() }),
  itemId: Id.nullable(),
  itemLabel: Text(300).nullable(),
  description: Text(500),
  kind: z.enum(DELIVERABLE_KINDS),
  quantity: z.number().int().nullable(),
  dueDate: Day.nullable(),
  status: z.enum(DELIVERABLE_STATUSES),
  sponsorStatus: z.enum(['not_started', 'submitted', 'needs_revision', 'approved']),
  submittedAt: Text(40).nullable(),
  reviewedAt: Text(40).nullable(),
  owner: z.object({ sub: z.string().max(200), name: Text(200).nullable() }).nullable(),
  evidence: z.object({ kind: Text(40), label: Text(200).nullable(), url: Text(2000).nullable(), ref: Text(200).nullable(), at: Iso.nullable() }).nullable(),
  origin: Text(40).nullable(),
  updatedAt: Iso.nullable(),
})
export type BookingDeliverable = z.infer<typeof BookingDeliverable>

export const BookingDeliverablesBoard = z.object({ version: z.literal(1), asOf: Iso, truncated: z.boolean(), deliverables: z.array(BookingDeliverable) })
export type BookingDeliverablesBoard = z.infer<typeof BookingDeliverablesBoard>
export const BookingSponsorshipDeliverables = z.object({ version: z.literal(1), asOf: Iso, sponsorshipId: Id, recordVersion: Version, deliverables: z.array(BookingDeliverable) })
export type BookingSponsorshipDeliverables = z.infer<typeof BookingSponsorshipDeliverables>

export type DeliverableBucket = 'overdue' | 'due_soon' | 'later' | 'no_date' | 'done'
export const DUE_SOON_DAYS = 14

export function deliverableBucket(d: Pick<BookingDeliverable, 'status' | 'dueDate'>, today: string): DeliverableBucket {
  if (d.status === 'fulfilled' || d.status === 'waived') return 'done'
  if (!d.dueDate) return 'no_date'
  if (d.dueDate < today) return 'overdue'
  if (d.dueDate <= addDays(today, DUE_SOON_DAYS)) return 'due_soon'
  return 'later'
}

export function fulfillmentSummary(list: Pick<BookingDeliverable, 'status' | 'dueDate' | 'sponsorStatus'>[], today: string) {
  const counted = list.length
  const fulfilled = list.filter((d) => d.status === 'fulfilled').length
  const waived = list.filter((d) => d.status === 'waived').length
  const owed = counted - waived
  return {
    total: counted,
    fulfilled,
    waived,
    open: counted - fulfilled - waived,
    overdue: list.filter((d) => deliverableBucket(d, today) === 'overdue').length,
    awaitingReview: list.filter((d) => d.sponsorStatus === 'submitted' && d.status !== 'fulfilled' && d.status !== 'waived').length,
    // Of what is owed (waived ones aren't), how much is fulfilled; null with nothing owed.
    rateBps: owed > 0 ? Math.round((fulfilled / owed) * 10000) : null,
  }
}

// Hub's evidence for a deliverable (Hub's own table; Booking keeps only a
// short reference so myBRE can show the sponsor "fulfilled, with proof").
export const HubDeliverableEvidence = z.object({
  id: z.string(),
  sponsorshipId: Id,
  deliverableId: Id,
  kind: z.enum(EVIDENCE_KINDS),
  label: z.string(),
  documentId: z.string().nullable(),
  url: z.string().nullable(),
  btowntixOrderId: z.string().nullable(),
  btowntixOrderNumber: z.string().nullable(),
  verified: z.object({ at: z.string(), status: z.string().nullable(), amountMinor: z.number().int().nullable(), tickets: z.number().int().nullable(), title: z.string(), subtitle: z.string().nullable() }).nullable(),
  addedByName: z.string().nullable(),
  addedAt: z.string(),
  removedAt: z.string().nullable(),
})
export type HubDeliverableEvidence = z.infer<typeof HubDeliverableEvidence>

// ------------------------------------------- BTownTix comp order evidence

// Sponsor ticket allotments (decision 9): a deliverable whose evidence is a
// comp order made in BTownTix. BTownTix has no command for making comp
// orders from Hub, so staff make the order in BTownTix and Hub checks it
// through BTownTix's existing lookup read: the number must find exactly
// that order, it must have collected no money (a comp), and it must not be
// canceled. Nothing is written to BTownTix.
export const ORDER_NUMBER = /^[A-Za-z0-9-]{4,40}$/
const norm = (n: string) => n.replace(/[\s-]/g, '').toUpperCase()

export type CompOrderCheck =
  | { ok: true; order: { sourceId: string; number: string; title: string; subtitle: string | null; status: string | null; amountMinor: number; tickets: number | null; url: string | null } }
  | { ok: false; code: 'NOT_FOUND' | 'NOT_A_COMP' | 'CANCELED' | 'AMBIGUOUS' | 'INVALID'; message: string }

const CANCELED = new Set(['CANCELED', 'CANCELLED', 'VOID', 'VOIDED', 'REFUNDED', 'EXPIRED', 'ABANDONED'])

export function checkCompOrder(orderNumber: string, lookup: Pick<LookupResult, 'hits'>): CompOrderCheck {
  if (!ORDER_NUMBER.test(orderNumber.trim())) return { ok: false, code: 'INVALID', message: 'Enter the BTownTix order number as shown on the order.' }
  const want = norm(orderNumber)
  const orders = lookup.hits.filter((h: LookupHit) => h.kind === 'order' && norm(h.title.replace(/^Order\s+/i, '')) === want)
  if (orders.length === 0) return { ok: false, code: 'NOT_FOUND', message: `BTownTix has no order ${orderNumber.trim()}.` }
  if (orders.length > 1) return { ok: false, code: 'AMBIGUOUS', message: 'BTownTix answered with more than one order for that number.' }
  const o = orders[0]!
  if (o.status && CANCELED.has(o.status.toUpperCase())) return { ok: false, code: 'CANCELED', message: `BTownTix order ${orderNumber.trim()} is ${o.status.toLowerCase()}.` }
  if (o.amountMinor === null || o.amountMinor !== 0) return { ok: false, code: 'NOT_A_COMP', message: `BTownTix order ${orderNumber.trim()} collected ${o.amountMinor === null ? 'an unknown amount' : `$${(o.amountMinor / 100).toFixed(2)}`}; a sponsor allotment is a comp order (no money collected).` }
  const tickets = o.subtitle?.match(/(\d+) tickets?/)
  return { ok: true, order: { sourceId: o.sourceId, number: orderNumber.trim(), title: o.title, subtitle: o.subtitle, status: o.status, amountMinor: 0, tickets: tickets ? Number(tickets[1]) : null, url: o.url } }
}
